import React, { useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { useParams, Navigate, Link } from 'react-router-dom';
import { CheckCircle, CreditCard, Clock, Info } from 'lucide-react';
import { getAppSettings } from '@/api/settings';
import { supabase } from '@/lib/supabaseClient';
import { useSettings } from '@/contexts/SettingsContext';

interface PaymentAuthorization {
  id: string;
  stripe_payment_intent_id: string;
  amount: number;
  currency: string;
  status: string;
  authorized_at: string;
  expires_at: string;
}

interface Order {
  id: string;
  display_id: number;
  total_amount: number;
  status: string;
  delivery_date: string;
  delivery_time: string;
}

interface DebugInfo {
  context: {
    settingsFromContext: string;
    settingsFromAPI: string;
  } | null;
  directQuery: {
    data: any;
    error: string | null;
  } | null;
  rpcQuery: {
    data: any;
    error: string | null;
  } | null;
  error: string | null;
}

export function OrderConfirmation() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [localConfirmationText, setLocalConfirmationText] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [paymentAuth, setPaymentAuth] = useState<PaymentAuthorization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [debug, setDebug] = useState<DebugInfo>({
    context: null,
    directQuery: null,
    rpcQuery: null,
    error: null
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch settings
        const appSettings = await getAppSettings();
        setLocalConfirmationText(appSettings.orderConfirmationText);

        // Fetch order details
        if (orderId) {
          // Check if orderId is a Stripe PaymentIntent ID (starts with 'pi_')
          const isPaymentIntentId = orderId.startsWith('pi_');

          let orderData: Order | null = null;
          let authData: PaymentAuthorization | null = null;

          if (isPaymentIntentId) {
            // If it's a PaymentIntent ID, find the payment authorization first (always exists)
            const { data: paymentAuthData, error: authError } = await supabase
              .from('payment_authorizations')
              .select('id, stripe_payment_intent_id, amount, currency, status, authorized_at, expires_at, order_id')
              .eq('stripe_payment_intent_id', orderId)
              .maybeSingle(); // Use maybeSingle instead of single to avoid PGRST116 error

            if (authError) {
              logger.error('Error fetching payment authorization:', authError);
            } else if (paymentAuthData) {
              authData = paymentAuthData;

              // Now fetch the order using order_id from payment authorization (if it exists)
              if (paymentAuthData.order_id) {
                const { data: fetchedOrder, error: orderError } = await supabase
                  .from('orders')
                  .select('id, display_id, total_amount, status, delivery_date, delivery_time')
                  .eq('id', paymentAuthData.order_id)
                  .maybeSingle(); // Use maybeSingle instead of single

                if (orderError) {
                  logger.error('Error fetching order:', orderError);
                } else if (fetchedOrder) {
                  orderData = fetchedOrder;
                }
              }
              // If order_id is NULL, that means webhook hasn't created the order yet
              // This is expected and we'll show a "processing" message
            }
          } else {
            // If it's a UUID, fetch order directly
            const { data: fetchedOrder, error: orderError } = await supabase
              .from('orders')
              .select('id, display_id, total_amount, status, delivery_date, delivery_time')
              .eq('id', orderId)
              .maybeSingle(); // Use maybeSingle instead of single

            if (orderError) {
              logger.error('Error fetching order:', orderError);
            } else if (fetchedOrder) {
              orderData = fetchedOrder;

              // Fetch payment authorization via order_id
              const { data: paymentAuthData, error: authError } = await supabase
                .from('payment_authorizations')
                .select('id, stripe_payment_intent_id, amount, currency, status, authorized_at, expires_at')
                .eq('order_id', fetchedOrder.id)
                .maybeSingle(); // Use maybeSingle instead of single

              if (authError) {
                logger.error('Error fetching payment authorization:', authError);
              } else if (paymentAuthData) {
                authData = paymentAuthData;
              }
            }
          }

          // Set the state with fetched data
          if (orderData) {
            setOrder(orderData);
          }
          if (authData) {
            setPaymentAuth(authData);
          }
        }

        // RPC query for debug
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_setting', { p_key: 'order_confirmation_text' });

        setDebug({
          context: {
            settingsFromContext: settings.orderConfirmationText,
            settingsFromAPI: appSettings.orderConfirmationText
          },
          directQuery: {
            data: null,
            error: "Direct access not available for app_settings schema"
          },
          rpcQuery: {
            data: rpcData,
            error: rpcError ? rpcError.message : null
          },
          error: null
        });
      } catch (err: any) {
        logger.error('Error fetching confirmation text:', err);
        setDebug(prev => ({
          ...prev,
          error: err.message || 'Unknown error'
        }));
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [settings, orderId]);

  if (!orderId) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Laddar beställningsinformation...</p>
        </div>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center">
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>
          <h1 className="mt-4 text-3xl font-extrabold text-gray-900 mb-2">
            Tack för din beställning!
          </h1>
          <p className="mt-2 text-lg text-gray-500">
            {order?.display_id
              ? `Din beställning #${order.display_id} har bekräftats`
              : 'Din order skapas just nu...'}
          </p>
          {!order && paymentAuth && (
            <p className="mt-2 text-sm text-gray-400">
              Din betalning är godkänd och din order skapas automatiskt. Detta tar bara några sekunder.
            </p>
          )}
          <p className="mt-4 text-gray-500">
            {settings.orderConfirmationText || localConfirmationText}
          </p>
        </div>

        {/* Payment Information Section */}
        {paymentAuth && (
          <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 bg-green-50">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                Betalningsinformation
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Din betalning har auktoriserats framgångsrikt
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {paymentAuth.status === 'succeeded' ? 'Auktoriserad' :
                       paymentAuth.status === 'captured' ? 'Betald' :
                       paymentAuth.status}
                    </span>
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Auktoriserat belopp</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    {formatCurrency(paymentAuth.amount)}
                  </dd>
                </div>
                {order && (
                  <>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Ordersumma</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatCurrency(order.total_amount)}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Leveransdatum</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(order.delivery_date).toLocaleDateString('sv-SE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </dd>
                    </div>
                  </>
                )}
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Auktoriserad
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(paymentAuth.authorized_at)}
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Utgår
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(paymentAuth.expires_at)}
                  </dd>
                </div>
              </dl>

              {/* Information box about 110% authorization */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Om din betalning
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        Vi har reserverat {formatCurrency(paymentAuth.amount)} på ditt kort (110% av ordervärdet).
                        Ingen betalning dras förrän leverans är bekräftad och slutgiltig vikt är fastställd.
                        Överskottet kommer automatiskt att släppas.
                      </p>
                      {paymentAuth.status === 'succeeded' && (
                        <p className="mt-2">
                          Reservationen är aktiv i 7 dagar och kommer automatiskt att släppas om ordern inte bekräftas.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            Fortsätt handla
          </Link>
        </div>
        
        {/* Debug section - only visible in development */}
        {import.meta.env.DEV && (
          <div className="mt-12 text-left">
            <details>
              <summary className="text-gray-500 cursor-pointer">Debug Information</summary>
              <div className="mt-4 bg-gray-100 p-4 rounded overflow-auto">
                <pre>{JSON.stringify(debug, null, 2)}</pre>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}